import React, { useState, useEffect, useRef } from "react";
import "./App.css";

function App() {
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true); // Chatbot always open
  const [paused, setPaused] = useState(false);
  const [userName, setUserName] = useState("");
  const [nameSubmitted, setNameSubmitted] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState("");
  // 🌐 BASE URL FOR IIS / LOCALHOST
  const API_BASE = `${window.location.origin}/api`;
  const msgEndRef = useRef(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const voiceEnabledRef = useRef(true);

  useEffect(() => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN"; // handles English + Marathi reasonably
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setListening(true);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.onerror = (event) => {
      console.error("Voice recognition error:", event.error);
      setListening(false);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.trim();
      if (transcript) {
        sendVoiceMessage(transcript);
      }
    };

    recognitionRef.current = recognition;
  }, []);
  
  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);

useEffect(() => {
  const handleBeforeUnload = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, []);


  const sendVoiceMessage = async (voiceText) => {
    setCategories([]);
    setSchemes([]);
    setSelectedCategory("");
    setSelectedScheme("");

    // 👤 Add as user message
    setChat((prev) => [...prev, { role: "user", text: voiceText }]);
    setLoading(true);

    try {
      // const res = await fetch("http://127.0.0.1:5000/chat", {
        const res = await fetch(`${API_BASE}/chat`, {

        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: voiceText }),
      });

      const data = await res.json();
      typeMessage((data.answer || "").replace(/\\n/g, "\n"));
    } catch {
      typeMessage("Server error. Try again.");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!window.speechSynthesis) return;

    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  // -----------------------------
  // 🔊 TEXT TO SPEECH (TTS)
  // -----------------------------
const speakText = (text, lang = "en-IN") => {
  if (!voiceEnabledRef.current || !text.trim()) return;

  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;

  const voice = getVoiceByLang(lang);
  if (voice) utterance.voice = voice;

  // 🔊 LANGUAGE-AWARE SPEED
  if (lang === "mr-IN" || lang === "hi-IN") {
    utterance.rate = 1.1;   // 👈 clear Marathi
  } else {
    utterance.rate = 1.9;   // 👈 perfect English
  }

  utterance.pitch = 1;
  window.speechSynthesis.speak(utterance);
};

  const getVoiceByLang = (lang) => {
    const voices = window.speechSynthesis.getVoices();

    // 🇮🇳 Marathi → Hindi Female Voice
    if (lang === "mr-IN") {
      return voices.find(
        (v) =>
          v.lang === "hi-IN" &&
          (v.name.toLowerCase().includes("female") ||
            v.name.toLowerCase().includes("हिन्दी") ||
            v.name.toLowerCase().includes("google"))
      );
    }

    // 🇮🇳 English India → Female
    if (lang === "en-IN") {
      return voices.find(
        (v) => v.lang === "en-IN" && v.name.toLowerCase().includes("heera")
      );
    }

    // 🌍 Fallback → any female voice
    return voices.find(
      (v) =>
        v.name.toLowerCase().includes("female") ||
        v.name.toLowerCase().includes("zira")
    );
  };

  // Detect whether text is Marathi (Devanagari script)
  const isMarathi = (text) => {
    const devanagari = /[\u0900-\u097F]/;
    return devanagari.test(text);
  };


  // Auto-scroll
  useEffect(() => {
    if (msgEndRef.current) {
      msgEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat, loading]);

  const extractRemarksForUI = (text) => {
    if (!text) return { remark: "", cleanText: "" };

    const sourceKeywords = [
      "fetched from the web",
      "answer sourced from the web",
      "not found in database",
      "not from the meda database",
      "based on meda database",
      "specific data not available",
    ];

    const lines = text.split("\n");
    const remarkLines = [];
    const answerLines = [];

    lines.forEach((line) => {
      const lower = line.toLowerCase();
      if (sourceKeywords.some((k) => lower.includes(k))) {
        remarkLines.push(line.trim());
      } else {
        answerLines.push(line);
      }
    });

    return {
      remark: remarkLines.join(" • "), // 🔑 ONE combined remark
      cleanText: answerLines.join("\n").trim(),
    };
  };

  // FETCH SCHEMES
  const fetchSchemes = (category) => {
    setSelectedCategory(category);
    // fetch(`http://127.0.0.1:5000/get_schemes?category=${category}`)
      fetch(`${API_BASE}/get_schemes?category=${encodeURIComponent(category)}`)
      .then((res) => res.json())
      .then((data) => setSchemes(data.schemes))
      .catch(console.error);
  };

  // FETCH SCHEME DETAILS

  const fetchSchemeDetails = (scheme) => {
    setSelectedScheme(scheme);
    setSchemes([]);

    setChat((prev) => [
      ...prev,
      { role: "user", text: `Tell me about ${scheme}` },
    ]);

    // fetch("http://127.0.0.1:5000/scheme_details", {
      fetch(`${API_BASE}/scheme_details`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scheme }),
    })
      .then((res) => res.json())
      .then((data) => {
        typeMessage((data.answer || "").replace(/\\n/g, "\n"));
      })
      .catch(() => {
        typeMessage("Error fetching scheme details.");
      });
  };

  

// const typeMessage = (text) => {
//   const { remark, cleanText } = extractRemarksForUI(text);
//   let idx = 0;

//   setChat((prev) => [
//     ...prev,
//     {
//       role: "bot",
//       remark,
//       text: "",
//       complete: false,
//       translated: "",
//       translating: false,
//     },
//   ]);

//   const interval = setInterval(() => {
//     if (idx < cleanText.length) {
//       setChat((prev) => {
//         const updated = [...prev];
//         updated[updated.length - 1].text = cleanText.slice(0, idx + 1);
//         return updated;
//       });
//       idx++;
//     } else {
//       clearInterval(interval);

//       setChat((prev) => {
//         const updated = [...prev];
//         updated[updated.length - 1].complete = true;
//         return updated;
//       });

//       // 🔊 SPEAK ONLY ONCE – AFTER FULL TEXT
//       if (voiceEnabledRef.current) {
//         speakText(
//           cleanText,
//           isMarathi(cleanText) ? "mr-IN" : "en-IN"
//         );
//       }
//     }
//   }, 15);
// };

const typeMessage = (text) => {
  const { remark, cleanText } = extractRemarksForUI(text);

  setChat((prev) => [
    ...prev,
    {
      role: "bot",
      remark,
      text: cleanText, // 👈 full text at once
      complete: true,
      translated: "",
      translating: false,
    },
  ]);

  // 🔊 Speak once
  if (voiceEnabledRef.current) {
    speakText(
      cleanText,
      isMarathi(cleanText) ? "mr-IN" : "en-IN"
    );
  }
};

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
  };


  const translateMessage = async (text, index) => {
    try {
      setChat((prev) => {
        const updated = [...prev];
        updated[index].translating = true;
        return updated;
      });

      const targetLang = isMarathi(text) ? "english" : "marathi";

      // const res = await fetch("http://127.0.0.1:5000/translate", {
        const res = await fetch(`${API_BASE}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, target: targetLang }),
      });

      const data = await res.json();

      setChat((prev) => {
        const updated = [...prev];
        updated[index].translated = data.translated;
        updated[index].translating = false;
        return updated;
      });
      speakText(data.translated, targetLang === "marathi" ? "mr-IN" : "en-IN");
    } catch (e) {
      alert("Translation error: " + e.message);
    }
  };

  // SEND MESSAGE
  const sendMessage = async () => {
    if (!message.trim()) return;

    setCategories([]);
    setSchemes([]);
    setSelectedCategory("");
    setSelectedScheme("");

    const userMsg = message;
    setMessage("");

    setChat((prev) => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      // const res = await fetch("http://127.0.0.1:5000/chat", {
        const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg }),
      });

      const data = await res.json();
      typeMessage((data.answer || "").replace(/\\n/g, "\n"));
    } catch {
      typeMessage("Server error. Try again.");
    }

    setLoading(false);
  };

  const getPreferredVoice = (lang) => {
  const voices = window.speechSynthesis.getVoices();

  // Prefer female Indian voice
  return (
    voices.find(v => v.lang === lang && v.name.toLowerCase().includes("female")) ||
    voices.find(v => v.lang === lang) ||
    voices.find(v => v.lang.startsWith("en")) ||
    voices[0]
  );
};


const handleNameSubmit = () => {
  if (!userName.trim()) return;

  const greetingText =
    `👋 नमस्कार ${userName}!\n` +
    `Welcome to MEDA Support.`;

  const assistText =
    "How can I assist you today?";

  setChat([
    {
      role: "bot",
      type: "greeting",
      text: greetingText,
    },
  ]);

  setNameSubmitted(true);

window.speechSynthesis.cancel();

// 1️⃣ Speak greeting using SAME engine
speakText(
  `नमस्कार ${userName}. Welcome to MEDA Support.`,
  "mr-IN"
);

// 2️⃣ After greeting finishes → next message
setTimeout(() => {
  typeMessage("How can I assist you today?");
}, 4500);
};


  return (
    <div className="chat-container">
      {/* HEADER */}
      <div className="chat-header">
        <span>MEDA Support (BETA)</span>
        <button
          className="voice-toggle-btn"
          style={{ fontSize: "14px" }}
          onClick={() => {
            if (voiceEnabled) {
              stopSpeaking(); // ⛔ stop immediately
            }
            setVoiceEnabled((prev) => !prev);
          }}
        >
          {voiceEnabled ? "🔊 Voice On" : "🔇 Voice Off"}
        </button>

        <button
          className="reset-btn"
          onClick={() => {
            setCategories([]);
            setSchemes([]);
            setSelectedCategory("");
            setSelectedScheme("");

            setChat([
              {
                role: "bot",
                type: "greeting",
                text:
                  "👋 Welcome to MEDA Support!\n" +
                  "How can I assist you today?\n\n" +
                  "Please type your query below.\n" +
                  "आपला प्रश्न खालील टेक्स्ट बॉक्समध्ये लिहा.",
              },
            ]);
          }}
        >
          Reset
        </button>
      </div>

      {/* CHAT MESSAGES */}
      <div className="chat-messages">
        {!nameSubmitted ? (
          // 👤 NAME INPUT CARD
          <div
            className="name-card"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "10%",
            }}
          >
            <img src="bot.png" alt="Bot" style={{ width: "208px", marginBottom:'12px', textAlign:'center' }} />

            <div
              className="name-card-content"
              style={{
                marginTop: "20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "16px",
                  textAlign: "center",
                }}
              >
                {" "}
                Welcome to MEDA Support
              </span>
              <p style={{margin:'2px'}}>Please enter your name to continue</p>

              <input
                type="text"
                placeholder="Enter your name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSubmit()}
                style={{border:'1px solid #111', padding:'10px'}}
              />

              <button style={{backgroundColor:'dodgerblue', padding:'7px', border:'1px solid dodgerblue', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px', color:'#FFF', borderRadius:'8px', cursor:'pointer'}} onClick={handleNameSubmit}>Start</button>
            </div>
          </div>
        ) : (
          <>
            {chat.map((msg, i) => (
              <div key={i}>
                {msg.type === "greeting" ? (
                  <div className="greeting-card">
                    <div className="greeting-avatar">
                      <img src="bot.png" alt="Bot" />
                    </div>
                    <div className="greeting-text">{msg.text}</div>
                  </div>
                ) : msg.role === "bot" ? (
                  <div className="bot-wrapper">
                    <img src="bot.png" alt="Bot" />
                    <div className="bot-msg">
                      {/* 🔔 REMARK — ALWAYS AT TOP */}
                      {msg.remark && (
                        <div className="remarks-box">{msg.remark}</div>
                      )}

                      {/* 📝 MAIN ANSWER (typed text) */}
                      <div className="answer-text">{msg.text}</div>

                      {/* TRANSLATE BUTTON */}
                      {msg.complete && !msg.translated && !msg.translating && (
                        <div
                          className="translate-btn"
                          onClick={() => translateMessage(msg.text, i)}
                        >
                          {isMarathi(msg.text)
                            ? "Translate to English"
                            : "Translate to Marathi"}
                        </div>
                      )}

                      {/* TRANSLATING LOADER */}
                      {msg.translating && (
                        <div className="translating">⏳ Translating...</div>
                      )}

                      {/* TRANSLATED TEXT */}
                      {msg.translated && (
                        <div className="bot-msg translated">
                          {msg.translated}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="user-wrapper">
                    <div className="user-msg">{msg.text}</div>
                    <img src="image.png" alt="User" />
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* BOT IS TYPING */}
        {loading && (
          <div className="bot-wrapper">
            <img src="bot.png" alt="Bot" />
            <div className="bot-msg">Typing...</div>
          </div>
        )}

        <div ref={msgEndRef} />
      </div>

      {/* CATEGORY MENU */}
      {categories.length > 0 && !selectedCategory && (
        <div className="menu-box">
          <strong>Select Category:</strong>
          {categories.map((cat, i) => (
            <div
              key={i}
              className="scheme-bubble"
              onClick={() => fetchSchemes(cat)}
            >
              {cat}
            </div>
          ))}
        </div>
      )}

      {/* SCHEME MENU */}
      {schemes.length > 0 && !selectedScheme && (
        <div className="menu-box">
          <strong>Select Scheme:</strong>
          {schemes.map((sc, i) => (
            <div
              key={i}
              className="scheme-bubble-internal"
              onClick={() => fetchSchemeDetails(sc)}
            >
              {sc}
            </div>
          ))}

          <div
            className="back-button"
            onClick={() => {
              setSchemes([]);
              setSelectedCategory("");
            }}
          >
            ← Back
          </div>
        </div>
      )}

      {nameSubmitted && (
        <div className="chat-input">
          <input
            type="text"
            value={message}
            placeholder="Type your message..."
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            disabled={loading}
          />

          {/* 🎤 VOICE SEARCH BUTTON */}
          <button
            className={`voice-btn ${listening ? "active" : ""}`}
            onClick={() => {
              stopSpeaking();
              recognitionRef.current && recognitionRef.current.start();
            }}

            disabled={listening || loading}
          >
            🎤
          </button>

          <button className="send-btn" onClick={sendMessage} disabled={loading}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

export default App;
